import {
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import SearchSelect from 'react-select';
import Theme from '../ui/Theme';
import service from '../../apis/service';
import EmployeeJourneyTabContent from './EmployeeJourneyTabContent';
import {
  getAssignedJourneys,
  getDepartment,
  getEmployeeList,
  getSelectedRoleID,
  showProgress,
} from '../../redux/actions/DealershipActions';
import InfoCircleIcon from '../../assets/InfoCircle';
import RuleConditionsPopup from '../customGroups/RuleConditionsPopup';
import { ErrorToast } from '../utils/Utils';
import { apiEndpoints } from '../../constants/apiEndpoints';

const useStyles = makeStyles(() => ({
  selectTopBar: {
    border: `1px solid ${Theme.palette.secondary.main}`,
    borderRadius: '0',
    height: '50px',
    width: 'inherit',
  },
  smallRadioButton: {
    '& svg': {
      width: '0.8em',
      height: '0.8em',
    },
  },
  radioButton: {
    height: '5px',
    width: '5px',
  },
  assignToText: {
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: '12px',
    lineHeight: '140%',
    marginRight: '20px',
    color: 'rgba(0, 0, 0, 0.4)',
    textTransform: 'uppercase',
  },
  form: {
    marginLeft: '20px',
    marginTop: '10px',
  },
  infoIconGrid: {
    marginTop: '10px',
    marginLeft: '10px',
    marginRight: '10px',
  },
}));

export default function CustomGroupDealershipContent() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const dealershipCustomGroups = useSelector((state) => state.getCustomGroupsForDealership?.dealershipCustomGroups);
  const dealershipRules = useSelector((state) => state.getRulesForDealership?.dealershipRules);
  const dealershipDetails = useSelector((state) => state.DealershipDetails.dealershipdetails);

  const [radioButtonValue, setRadioButtonValue] = useState('group');
  const [selectedValue, setSelectedValue] = useState({});
  const [showRulePopup, setShowRulePopup] = useState(false);
  const [ruleDetails, setRuleDetails] = useState(null);

  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    dispatch(getDepartment(0));
    dispatch(getSelectedRoleID(0));
  }, []);

  useEffect(() => {
    if (radioButtonValue === 'group') {
      setSelectedValue(dealershipCustomGroups?.length ? dealershipCustomGroups[0] : {});
    } else {
      setSelectedValue(dealershipRules?.length ? dealershipRules[0] : {});
    }
  }, [dealershipCustomGroups, dealershipRules, radioButtonValue]);

  useEffect(() => {
    setTabValue(0);
  }, [radioButtonValue]);

  const getGroupEmployees = async (id) => {
    dispatch(showProgress(true));
    try {
      const res = await service.get(
        apiEndpoints.DEALER_CG_EMP_LIST.replace('ID', id).replace('DEALER_ID', dealershipDetails.id),
      );
      dispatch(getEmployeeList('fetch', res));
    } catch (error) {
      console.log(error);
    }
    dispatch(showProgress(false));
  };

  const getRuleEmployeesAndJourneys = async (id) => {
    dispatch(showProgress(true));
    try {
      const res = await service.get(
        apiEndpoints.DEALER_RULE_EMPLOYEES.replace('ID', id).replace('DEALER_ID', dealershipDetails.id),
      );
      dispatch(getEmployeeList('fetch', res));
    } catch (error) {
      console.log(error);
    }

    try {
      const res = await service.get(
        apiEndpoints.DEALER_RULE_JOURNEYS.replace('ID', id).replace('DEALER_ID', dealershipDetails.id),
      );
      dispatch(getAssignedJourneys(res));
    } catch (error) {
      console.log(error);
    }
    dispatch(showProgress(false));
  };

  useEffect(() => {
    refreshData();
  }, [selectedValue]);

  const refreshData = () => {
    if (!_isEmpty(selectedValue)) {
      dispatch(getEmployeeList('fetch', []));
      dispatch(getAssignedJourneys([]));

      if (radioButtonValue === 'group') {
        getGroupEmployees(selectedValue.id);
      } else {
        getRuleEmployeesAndJourneys(selectedValue.id);
      }
    } else {
      dispatch(getEmployeeList('fetch', []));
      dispatch(getAssignedJourneys([]));
    }
  };

  const options = useMemo(() => {
    if (radioButtonValue === 'group') return dealershipCustomGroups;
    return dealershipRules;
  }, [radioButtonValue, dealershipCustomGroups, dealershipRules]);

  async function readRulesId(id) {
    dispatch(showProgress(true));
    try {
      const response = await service.get(apiEndpoints.DEALER_RULE.replace('ID', id));
      setRuleDetails(response);
      setShowRulePopup(true);
      dispatch(showProgress(false));
    } catch (error) {
      dispatch(showProgress(false));
      ErrorToast(error);
    }
  }

  const handleRuleInfoClick = () => {
    setRuleDetails(null);
    if (selectedValue?.id) {
      readRulesId(selectedValue.id);
    }
  };

  return (
    <>
      <Grid container direction="row" className={classes.selectTopBar} justifyContent="space-between">
        <Grid item>
          <FormControl className={classes.form}>
            <RadioGroup
              row
              onChange={(e) => {
                setSelectedValue({});
                setRadioButtonValue(e.target.value);
              }}
              defaultValue="group"
              value={radioButtonValue}
            >
              <FormControlLabel
                value="group"
                control={<Radio className={classes.radioButton} color="primary" />}
                label={<Typography className={classes.journeyAssignText}> Group</Typography>}
                className={classes.smallRadioButton}
              />
              <FormControlLabel
                value="rule"
                control={<Radio className={classes.radioButton} color="primary" />}
                label={<Typography className={classes.journeyAssignText}> Rule</Typography>}
                className={classes.smallRadioButton}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item>
          <Grid container>
            <Grid item style={{ width: '300px', marginTop: '5px', marginRight: '10px' }}>
              <SearchSelect
                placeholder="Select Custom Group"
                value={selectedValue}
                onChange={(val) => setSelectedValue(val)}
                options={options}
                maxMenuHeight="200px"
                styles={{
                  control: (provided) => ({
                    ...provided,
                    backgroundColor: '#f4f4f4',
                  }),
                }}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                isClearable={false}
              />
            </Grid>
            {radioButtonValue === 'rule' ? (
              <Grid item className={classes.infoIconGrid}>
                <Tooltip title="Rule Info">
                  <InfoCircleIcon
                    width="23px"
                    height="23px"
                    style={{ cursor: 'pointer' }}
                    onClick={handleRuleInfoClick}
                  />
                </Tooltip>
              </Grid>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
      <EmployeeJourneyTabContent
        tabValue={tabValue}
        setTabValue={setTabValue}
        refreshData={refreshData}
        flow={radioButtonValue}
      />
      {showRulePopup && (
        <RuleConditionsPopup
          close={() => {
            setShowRulePopup(false);
            setRuleDetails(null);
          }}
          rule={ruleDetails}
        />
      )}
    </>
  );
}
